import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatEatDateTime } from '@wodi/domain';
import {
  CHECKLIST_ANSWERS,
  type ChecklistPhase,
  type ChecklistPhaseView,
  type TheatreCaseView,
} from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  Notice,
  RadioGroup,
  Select,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { recordChecklist } from '../api/theatre.ts';
import { UploadDocument } from '../documents/uploads.tsx';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';
import styles from './theatre.module.css';
import { ANSWER_WORDS, errorText, PHASE_ACTIONS, PHASE_WORDS } from './words.ts';

// The pre-op checklist and the WHO safety checklist steps (docs/plans/phase2-theatre.md §4.2,
// §6): each step recorded once, in order, with who and when, against the facility's own item
// wording. Placeholder wording is labelled until the clinical lead confirms it (T1).

type Answer = (typeof CHECKLIST_ANSWERS)[number];

const PHASE_NOTES: Partial<Record<ChecklistPhase, string>> = {
  sign_in:
    'Recording sign in checks payment and SHA approval for the procedure, takes the case into theatre and charges the theatre fee.',
  sign_out: 'Recording sign out completes the case and marks the procedure done.',
};

const CONSENT_FORM = 'form';
const CONSENT_NONE = 'none';

function RecordDialog({
  view,
  step,
  onClose,
}: {
  view: TheatreCaseView;
  step: ChecklistPhaseView;
  onClose: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [answers, setAnswers] = useState<Record<string, { answer: Answer | null; note: string }>>(
    () => Object.fromEntries(step.items.map((i) => [i.id, { answer: null, note: '' }])),
  );
  const [note, setNote] = useState('');
  const [consentWay, setConsentWay] = useState<string | null>(null);
  const [consentDocumentId, setConsentDocumentId] = useState<string | null>(null);
  const [consentReason, setConsentReason] = useState('');
  const preOp = step.phase === 'pre_op';
  const record = useMutation({
    mutationFn: () =>
      recordChecklist(view.id, step.phase, {
        answers: step.items.map((i) => {
          const a = answers[i.id];
          const text = a?.note.trim() ?? '';
          return { itemId: i.id, answer: a?.answer ?? 'yes', note: text === '' ? null : text };
        }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        ...(preOp && consentWay === CONSENT_FORM && consentDocumentId !== null
          ? { consentDocumentId }
          : {}),
        ...(preOp && consentWay === CONSENT_NONE
          ? { consentNotObtainedReason: consentReason.trim() }
          : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['theatre'] });
      toasts.show({ title: `${PHASE_WORDS[step.phase]} recorded` });
      onClose();
    },
  });
  const answered = step.items.every((i) => {
    const a = answers[i.id];
    return a?.answer != null && (a.answer !== 'no' || a.note.trim() !== '');
  });
  const consentReady =
    !preOp ||
    (consentWay === CONSENT_FORM && consentDocumentId !== null) ||
    (consentWay === CONSENT_NONE && consentReason.trim().length >= 5);
  const ready = online && answered && consentReady && !record.isPending;
  return (
    <Dialog
      title={PHASE_WORDS[step.phase]}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <form
        className={styles.form}
        aria-label={PHASE_WORDS[step.phase]}
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) record.mutate();
        }}
      >
        {step.items.some((i) => i.provisional) ? (
          <Notice tone="info">
            Placeholder wording, not confirmed by the clinical lead. Follow your facility's approved
            checklist until it is replaced in Settings, Theatres.
          </Notice>
        ) : null}
        {step.items.length === 0 ? (
          <p className={styles.meta}>No items are switched on for this step.</p>
        ) : null}
        {step.items.map((item) => {
          const a = answers[item.id] ?? { answer: null, note: '' };
          return (
            <div key={item.id} className={styles.itemBlock}>
              <RadioGroup
                label={item.text}
                orientation="horizontal"
                {...(item.requiresYes ? { description: 'Must be Yes to go on.' } : {})}
                value={a.answer ?? ''}
                onChange={(v) => {
                  setAnswers((prev) => ({
                    ...prev,
                    [item.id]: { answer: v as Answer, note: prev[item.id]?.note ?? '' },
                  }));
                }}
                options={CHECKLIST_ANSWERS.map((ans) => ({ id: ans, label: ANSWER_WORDS[ans] }))}
              />
              {a.answer === 'no' ? (
                <TextField
                  label="Why not"
                  value={a.note}
                  onChange={(text) => {
                    setAnswers((prev) => ({
                      ...prev,
                      [item.id]: { answer: prev[item.id]?.answer ?? null, note: text },
                    }));
                  }}
                />
              ) : null}
            </div>
          );
        })}
        {preOp ? (
          <div className={styles.form}>
            <RadioGroup
              label="Consent for the operation"
              value={consentWay ?? ''}
              onChange={setConsentWay}
              options={[
                {
                  id: CONSENT_FORM,
                  label: 'Signed consent form',
                  ...(view.consentForms.length === 0
                    ? { description: 'Upload the form first; none is on this visit yet.' }
                    : {}),
                },
                { id: CONSENT_NONE, label: 'Consent could not be obtained' },
              ]}
            />
            {consentWay === CONSENT_FORM ? (
              <Select
                label="Consent form"
                placeholder={view.consentForms.length === 0 ? 'None uploaded' : 'Choose one'}
                options={view.consentForms.map((f) => ({
                  id: f.id,
                  label: `${f.title}, ${formatEatDateTime(new Date(f.uploadedAt))}`,
                }))}
                value={consentDocumentId}
                onChange={(v) => {
                  setConsentDocumentId(typeof v === 'string' ? v : null);
                }}
              />
            ) : null}
            {consentWay === CONSENT_NONE ? (
              <TextArea
                label="Why consent could not be obtained"
                description="At least 5 characters. Kept with the case."
                value={consentReason}
                onChange={setConsentReason}
                rows={2}
              />
            ) : null}
          </div>
        ) : null}
        <TextArea label="Note (optional)" value={note} onChange={setNote} rows={2} />
        {PHASE_NOTES[step.phase] === undefined ? null : (
          <p className={styles.meta}>{PHASE_NOTES[step.phase]}</p>
        )}
        {record.error === null ? null : (
          <Notice tone="error">
            {errorText(record.error, `${PHASE_WORDS[step.phase]} wasn't recorded. Try again.`)}
          </Notice>
        )}
        <DialogActions>
          <Button variant="quiet" onPress={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!ready}
            isPending={record.isPending}
            pendingLabel="Saving…"
          >
            {PHASE_ACTIONS[step.phase]}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function Recorded({ step }: { step: ChecklistPhaseView }) {
  const r = step.recorded;
  if (r === null) return null;
  return (
    <>
      <p className={styles.meta}>
        {`Recorded ${formatEatDateTime(new Date(r.at))} by ${r.by.displayName}`}
      </p>
      <ul className={styles.answers} aria-label={`${PHASE_WORDS[step.phase]} answers`}>
        {r.responses.map((resp) => (
          <li key={resp.itemId} className={styles.answer}>
            <span className={styles.answerWord}>{ANSWER_WORDS[resp.answer]}</span>
            <span>
              {resp.text}
              {resp.note === null ? '' : `. ${resp.note}`}
            </span>
          </li>
        ))}
      </ul>
      {r.note === null ? null : <p className={styles.prose}>{r.note}</p>}
    </>
  );
}

export function ChecklistSection({
  view,
  mayRecord,
  mayUpload,
}: {
  view: TheatreCaseView;
  mayRecord: boolean;
  mayUpload: boolean;
}) {
  const online = useOnline();
  const [open, setOpen] = useState<ChecklistPhase | null>(null);
  const next = view.checklist.find((s) => s.recorded === null);
  const active = view.status === 'booked' || view.status === 'in_theatre';
  const openStep = view.checklist.find((s) => s.phase === open);
  return (
    <>
      <ol className={styles.steps} aria-label="Safety checklist">
        {view.checklist.map((step) => {
          const state =
            step.recorded !== null ? 'done' : step.phase === next?.phase ? 'next' : 'later';
          return (
            <li key={step.phase} className={styles.step} data-state={state}>
              <div className={styles.stepHead}>
                <h3 className={styles.stepTitle}>{PHASE_WORDS[step.phase]}</h3>
                {step.recorded !== null ? (
                  <Tag state="done">Recorded</Tag>
                ) : state === 'next' && active && mayRecord ? (
                  <Button
                    variant="primary"
                    isDisabled={!online}
                    onPress={() => {
                      setOpen(step.phase);
                    }}
                  >
                    {PHASE_ACTIONS[step.phase]}
                  </Button>
                ) : (
                  <Tag state="pending">Not recorded</Tag>
                )}
              </div>
              <Recorded step={step} />
            </li>
          );
        })}
      </ol>
      {view.consent === null ? null : (
        <p className={styles.meta}>
          {view.consent.documentId !== null
            ? `Consent: signed form "${view.consent.documentTitle ?? 'Consent form'}"`
            : `Consent not obtained: ${view.consent.notObtainedReason ?? ''}`}
        </p>
      )}
      {mayUpload && view.status === 'booked' && view.consent === null ? (
        <div className={styles.actions}>
          <UploadDocument
            visitId={view.visit.status === 'open' ? view.visit.id : null}
            listKeys={[['theatre', 'case', view.id]]}
            online={online}
          />
          <p className={styles.meta}>Upload the signed consent form as a consent form.</p>
        </div>
      ) : null}
      {openStep === undefined ? null : (
        <RecordDialog
          view={view}
          step={openStep}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </>
  );
}
